import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { OrganisationMembership } from '@ghostscreen/contracts';
import { formatAdminDate, formatAdminRole } from './adminFormat';

type AdminOrganisationCardProps = {
  membership: OrganisationMembership;
  onRename: (name: string) => void;
  renaming?: boolean;
  renameError?: string | null;
};

export function AdminOrganisationCard({
  membership,
  onRename,
  renaming = false,
  renameError = null,
}: AdminOrganisationCardProps) {
  const { t } = useTranslation(['admin', 'common']);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(membership.name);
  const [copied, setCopied] = useState<'id' | 'slug' | null>(null);

  useEffect(() => {
    setEditing(false);
    setName(membership.name);
  }, [membership.id, membership.name]);

  const copy = async (kind: 'id' | 'slug', value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(kind);
      window.setTimeout(() => setCopied(null), 1400);
    } catch {
      /* ignore */
    }
  };

  return (
    <section
      className="rounded-[14px] border border-line px-5 py-5"
      style={{ background: 'var(--gs-panel)' }}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="m-0 truncate text-xl font-extrabold tracking-[-0.03em] text-ink">
              {membership.name}
            </h2>
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em]"
              style={{
                background: 'color-mix(in srgb, var(--gs-ok) 16%, transparent)',
                color: 'var(--gs-ok)',
              }}
            >
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: 'var(--gs-ok)' }} />
              {t('organisation.activeTenant')}
            </span>
          </div>
          <p className="m-0 mt-1 text-sm text-muted">
            {t('organisation.slugLine', { slug: membership.slug })}
          </p>
        </div>
      </div>

      <dl className="mt-5 grid gap-4 sm:grid-cols-2">
        <div>
          <dt className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted">
            {t('organisation.idLabel')}
          </dt>
          <dd className="mt-1.5 flex items-center gap-2">
            <code
              className="min-w-0 flex-1 truncate rounded-[10px] border border-line px-3 py-2 text-[12px] text-ink"
              style={{ background: 'var(--gs-field)' }}
            >
              {membership.id}
            </code>
            <button
              type="button"
              onClick={() => void copy('id', membership.id)}
              className="shrink-0 rounded-[10px] border border-line px-2.5 py-2 text-xs font-semibold text-ink2"
            >
              {copied === 'id' ? t('common:actions.copied') : t('common:actions.copy')}
            </button>
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted">
            {t('organisation.slugLabel')}
          </dt>
          <dd className="mt-1.5 flex items-center gap-2">
            <code
              className="min-w-0 flex-1 truncate rounded-[10px] border border-line px-3 py-2 text-[12px] text-ink"
              style={{ background: 'var(--gs-field)' }}
            >
              {membership.slug}
            </code>
            <button
              type="button"
              onClick={() => void copy('slug', membership.slug)}
              className="shrink-0 rounded-[10px] border border-line px-2.5 py-2 text-xs font-semibold text-ink2"
            >
              {copied === 'slug' ? t('common:actions.copied') : t('common:actions.copy')}
            </button>
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted">
            {t('organisation.roleLabel')}
          </dt>
          <dd className="mt-1.5 text-sm font-semibold text-ink">
            {formatAdminRole(membership.role)}
          </dd>
        </div>
        <div>
          <dt className="text-[11px] font-bold uppercase tracking-[0.14em] text-muted">
            {t('organisation.createdLabel')}
          </dt>
          <dd className="mt-1.5 text-sm text-ink">{formatAdminDate(membership.createdAt)}</dd>
        </div>
      </dl>

      {editing ? (
        <form
          className="mt-5 space-y-3 rounded-[12px] border border-line bg-panel2 p-3"
          onSubmit={(event) => {
            event.preventDefault();
            const next = name.trim();
            if (!next || next === membership.name) {
              setEditing(false);
              setName(membership.name);
              return;
            }
            onRename(next);
          }}
        >
          <label className="block text-xs font-semibold text-muted">
            {t('organisation.nameLabel')}
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-1 w-full rounded-[10px] border border-line bg-field px-3 py-2.5 text-sm text-ink"
              autoFocus
            />
          </label>
          {renameError ? (
            <p className="m-0 text-sm" style={{ color: 'var(--gs-bad)' }} role="alert">
              {renameError}
            </p>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <button
              type="submit"
              disabled={renaming || !name.trim()}
              className="rounded-[10px] px-3.5 py-2 text-sm font-bold text-[var(--gs-accent-ink)] disabled:opacity-50"
              style={{ background: 'var(--gs-accent-solid)' }}
            >
              {renaming ? t('common:actions.saving') : t('organisation.saveName')}
            </button>
            <button
              type="button"
              disabled={renaming}
              onClick={() => {
                setEditing(false);
                setName(membership.name);
              }}
              className="rounded-[10px] border border-line px-3.5 py-2 text-sm font-semibold text-ink"
            >
              {t('common:actions.cancel')}
            </button>
          </div>
        </form>
      ) : (
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => {
              setName(membership.name);
              setEditing(true);
            }}
            className="rounded-[10px] border border-line px-3.5 py-2 text-sm font-semibold text-ink hover:border-[var(--gs-accent)]"
          >
            {t('organisation.rename')}
          </button>
          <button
            type="button"
            disabled
            title={t('organisation.billingUnavailable')}
            className="rounded-[10px] border border-line px-3.5 py-2 text-sm font-semibold text-muted opacity-70"
          >
            {t('organisation.billing')}
          </button>
        </div>
      )}
    </section>
  );
}
